<script lang="ts">
	import { goto } from '$app/navigation';
	import { resolve } from '$app/paths';
	import ArrowIcon from '$lib/components/marketing/redesign2025/ArrowIcon.svelte';
	import VoucherInput, { type AppliedVoucher } from '$lib/components/vouchers/VoucherInput.svelte';
	import PortSelection from '@/components/Forms/PortSelection.svelte';
	import { m } from '$lib/paraglide/messages.js';
	import { getLocale, localizeHref } from '@/paraglide/runtime';
	import {
		BoatsKindOptions,
		BoatsRegistryListOptions,
		BoatsStatusOptions,
		Collections,
		UsersRoleOptions,
		UsersStatusOptions,
		type BoatsRecord
	} from '@/pocketbase/pocketbase-types';
	import { currentUser, getErrorMessage, pb } from '@/utils/ApiClient';
	import ArrowLeft from '@lucide/svelte/icons/arrow-left';
	import CircleAlert from '@lucide/svelte/icons/circle-alert';
	import Sailboat from '@lucide/svelte/icons/sailboat';
	import Ship from '@lucide/svelte/icons/ship';
	import ShipWheel from '@lucide/svelte/icons/ship-wheel';
	import { ClientResponseError } from 'pocketbase';
	import posthog from 'posthog-js';
	import { onMount } from 'svelte';

	const labelClass = 'text-[0.86rem] font-semibold text-zp-ink';

	const inputClass =
		'w-full rounded-[10px] border-[1.5px] border-zp-line bg-white px-3.5 py-3 font-sans text-[0.98rem] text-zp-ink transition-[border-color,box-shadow] focus:border-zp-navy focus:outline-none focus:ring-[3px] focus:ring-zp-navy/10 disabled:opacity-60';

	const errorInputClass = 'border-red-500 focus:border-red-500 focus:ring-red-500/10';

	const portTriggerClass =
		'h-auto min-h-0 w-full justify-between rounded-[10px] border-[1.5px] border-zp-line bg-white px-3.5 py-3 pl-9 text-left text-[0.98rem] font-normal text-zp-ink shadow-none hover:bg-white hover:text-zp-ink focus-visible:border-zp-navy focus-visible:ring-[3px] focus-visible:ring-zp-navy/10 focus-visible:outline-none';

	const radioOptionClass =
		'flex items-center justify-center rounded-[10px] border-[1.5px] border-zp-line bg-white text-zp-ink transition-[border-color,box-shadow,background-color] hover:border-[#b8b3cc] peer-checked:border-zp-navy peer-checked:bg-zp-navy/5 peer-focus-visible:border-zp-navy peer-focus-visible:ring-[3px] peer-focus-visible:ring-zp-navy/10';

	const checkboxClass =
		"size-[19px] shrink-0 appearance-none rounded-md border-[1.5px] border-[#b8b3cc] bg-white checked:border-zp-green checked:bg-zp-green checked:bg-size-[100%_100%] checked:bg-[url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22white%22 stroke-width=%223%22%3E%3Cpath d=%22M20 6 9 17l-5-5%22/%3E%3C/svg%3E')] focus-visible:ring-[3px] focus-visible:ring-zp-navy/10 focus-visible:outline-none";

	const checkboxRowClass = 'flex gap-2.5 text-[0.83rem]';

	const checkboxLabelClass = 'cursor-pointer leading-snug text-[#403c63]';

	type FormError = 'existing_account' | 'add_boat' | 'otp' | null;
	type FieldKey = 'name' | 'port' | 'email' | 'acceptTerms' | 'otpCode';

	const locale = $derived(getLocale());

	const bullets = $derived.by(() => {
		void locale;
		return [m.ab_bullet1(), m.ab_bullet2(), m.ab_bullet3()];
	});

	let loading = $state(false);
	let formError = $state<FormError>(null);
	let formErrorDetail = $state('');

	const errorTitle = $derived.by(() => {
		switch (formError) {
			case 'existing_account':
				return m.ab_err_existing_title();
			case 'otp':
				return m.ab_err_otp_title();
			case 'add_boat':
				return m.ab_err_add_title();
			default:
				return '';
		}
	});

	const errorMessage = $derived.by(() => {
		switch (formError) {
			case 'existing_account':
				return m.ab_err_existing_msg();
			case 'otp':
				return m.ab_err_otp_msg() + formErrorDetail;
			case 'add_boat':
				return formErrorDetail;
			default:
				return '';
		}
	});

	let boat: BoatsRecord = $state({
		kind: BoatsKindOptions.sailboat,
		registryList: BoatsRegistryListOptions.seventh
	} as BoatsRecord);
	let emailAddress = $state('');
	let isAuthenticated = $state(pb.authStore.isValid);
	let showOTPForm = $state(false);
	let otpResult = $state<{ otpId: string } | null>(null);
	let otpCode = $state('');
	let acceptTerms = $state(false);
	let acceptNewsletter = $state(false);
	let appliedVoucher = $state<AppliedVoucher | null>(null);
	let fieldErrors = $state<Partial<Record<FieldKey, string>>>({});

	onMount(() => {
		return currentUser.subscribe(() => {
			isAuthenticated = pb.authStore.isValid;
		});
	});

	function inputClasses(field?: FieldKey) {
		return field && fieldErrors[field] ? `${inputClass} ${errorInputClass}` : inputClass;
	}

	function clearFieldError(field: FieldKey) {
		if (!(field in fieldErrors)) return;
		const next = { ...fieldErrors };
		delete next[field];
		fieldErrors = next;
	}

	function hasPort(): boolean {
		const port = boat.port;
		if (Array.isArray(port)) return port.length > 0 && !!port[0];
		return !!port;
	}

	function isEmailValid(email: string): boolean {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
	}

	function validateMainForm(): boolean {
		const err: Partial<Record<FieldKey, string>> = {};
		if (!boat.name?.trim()) err.name = m.ab_err_name_required();
		if (!hasPort()) err.port = m.ab_err_port_required();
		if (!isAuthenticated) {
			if (!emailAddress.trim()) err.email = m.ab_err_email_required();
			else if (!isEmailValid(emailAddress)) err.email = m.ab_err_email_invalid();
			if (!acceptTerms) err.acceptTerms = m.ab_err_terms_required();
		}
		fieldErrors = err;
		return Object.keys(err).length === 0;
	}

	function validateOtpForm(): boolean {
		const err: Partial<Record<FieldKey, string>> = {};
		if (!otpCode.trim()) err.otpCode = m.ab_err_otp_required();
		fieldErrors = err;
		return Object.keys(err).length === 0;
	}

	$effect(() => {
		if (hasPort() && fieldErrors.port) clearFieldError('port');
	});

	async function checkUserExists(email: string): Promise<boolean> {
		try {
			const result = await pb.collection(Collections.Users).getList(1, 1, {
				filter: `email="${email}"`
			});
			return result.totalItems > 0;
		} catch (error) {
			console.error('Error verificando usuario:', error);
			return false;
		}
	}

	async function createUser(email: string): Promise<string> {
		const randomPassword =
			Math.random().toString(36).slice(-12) +
			Math.random().toString(36).toUpperCase().slice(-12) +
			'!@#$%^&*()_+';

		const userData = {
			email,
			password: randomPassword,
			passwordConfirm: randomPassword,
			username: email.split('@')[0],
			role: UsersRoleOptions.user,
			status: UsersStatusOptions.active,
			verified: false,
			acceptNewsletter
		};

		const newUser = await pb.collection(Collections.Users).create(userData);

		posthog.identify(email, { email });
		posthog.capture('user_signed_up', { email });
		return newUser.id;
	}

	async function createBoatForOwner(ownerId: string) {
		boat.owner = [ownerId];
		boat.status = BoatsStatusOptions.pending;
		if (typeof boat.port === 'string') {
			boat.port = [boat.port];
		}
		const created = await pb.collection(Collections.Boats).create(boat);
		await redeemVoucherForBoat(created.id);
	}

	// El barco ya está creado: si el canje falla, avisamos sin bloquear el flujo.
	async function redeemVoucherForBoat(boatId: string) {
		if (!appliedVoucher) return;
		try {
			await pb.send('/api/vouchers/redeem-boat', {
				method: 'POST',
				body: { code: appliedVoucher.code, boatId }
			});
			posthog.capture('voucher_redeemed', { kind: appliedVoucher.kind, context: 'add_boat' });
		} catch (e) {
			console.warn('No se pudo canjear el código promocional:', e);
			const detail = (e as { response?: { error?: string } })?.response?.error;
			alert(
				`Tu barco se ha creado correctamente, pero el código promocional no se pudo aplicar${detail ? `: ${detail}` : '.'}`
			);
		}
	}

	async function handleSubmit(e: Event) {
		e.preventDefault();
		formError = null;
		formErrorDetail = '';
		if (!validateMainForm()) return;

		try {
			loading = true;

			if (isAuthenticated) {
				if (pb.authStore.record?.id) {
					await createBoatForOwner(pb.authStore.record.id);
					posthog.capture('boat_created', { email: pb.authStore.record?.email });
					goto(localizeHref('/cuenta/barcos'));
				} else {
					throw new Error('No se pudo obtener el ID del usuario');
				}
			} else {
				const userExists = await checkUserExists(emailAddress);
				if (!userExists) {
					await createUser(emailAddress);
				}
				otpResult = await pb.collection(Collections.Users).requestOTP(emailAddress);
				fieldErrors = {};
				showOTPForm = true;
			}
		} catch (error) {
			console.error(error);
			if (
				error instanceof ClientResponseError &&
				error.response?.data &&
				Object.values(error.response.data).some((data: { message?: string }) =>
					data.message?.includes('must be unique')
				)
			) {
				formError = 'existing_account';
			} else {
				formError = 'add_boat';
				formErrorDetail = getErrorMessage(error as ClientResponseError);
			}
		} finally {
			loading = false;
		}
	}

	async function verifyOTP(e: Event) {
		e.preventDefault();
		formError = null;
		formErrorDetail = '';
		if (!validateOtpForm()) return;

		try {
			if (!otpResult?.otpId) {
				throw new Error('No se ha solicitado un código OTP');
			}

			loading = true;

			const authData = await pb.collection(Collections.Users).authWithOTP(otpResult.otpId, otpCode);

			if (!authData.record.verified) {
				await pb.collection(Collections.Users).update(authData.record.id, {
					verified: true
				});
			}

			await createBoatForOwner(authData.record.id);
			goto(localizeHref('/cuenta/barcos'));
		} catch (error) {
			console.error(error);
			formError = 'otp';
			formErrorDetail = getErrorMessage(error as ClientResponseError);
		} finally {
			loading = false;
		}
	}

	function goBack() {
		if (showOTPForm) {
			showOTPForm = false;
			otpResult = null;
			otpCode = '';
			formError = null;
			formErrorDetail = '';
			fieldErrors = {};
		}
	}
</script>

<div class="zp-add-boat-stage">
	<div class="zp-add-boat-in">
		<!-- Pitch -->
		<div class="zp-add-boat-pitch min-w-0">
			<span
				class="inline-flex items-center gap-2.5 text-[1rem] leading-[1.12] font-semibold whitespace-nowrap text-zp-ink max-[720px]:text-[1rem]"
			>
				<span class="size-[7px] shrink-0 rounded-full bg-zp-accent"></span>
				{m.ab_eyebrow()}
			</span>
			<h1
				class="mt-[22px] text-[clamp(2.3rem,4vw,3.7rem)] leading-none tracking-[-0.02em] text-zp-ink"
			>
				<span class="block"
					>{m.ab_h1_before()}<em class="text-zp-navy italic">{m.ab_h1_em()}</em></span
				>
				<span class="block whitespace-nowrap">{m.ab_h1_line2()}</span>
			</h1>
			<p class="mt-5 max-w-[34ch] text-[clamp(1rem,1.25vw,1.18rem)] leading-normal text-[#3a3658]">
				{m.ab_pitch_p()}
			</p>
			<ul class="mt-[26px] flex list-none flex-col gap-[11px] p-0">
				{#each bullets as item (item)}
					<li class="flex items-center gap-[11px] text-[0.98rem] font-medium text-zp-prose">
						<span
							class="grid size-5 shrink-0 place-items-center rounded-full bg-zp-green/14 text-zp-green"
						>
							<svg
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="3"
								class="size-[11px]"
								aria-hidden="true"
							>
								<path d="M20 6 9 17l-5-5" />
							</svg>
						</span>
						{item}
					</li>
				{/each}
			</ul>
			<div
				class="mt-7 flex max-w-120 items-center gap-3.5 border-t border-zp-line pt-[22px] max-[820px]:mt-[22px] max-[820px]:pt-[18px]"
			>
				<div class="font-serif text-[2rem] leading-none whitespace-nowrap text-zp-navy">
					{m.ab_commission_pct()}<small
						class="font-sans text-[0.82rem] font-semibold text-zp-muted"
					>
						{m.ab_commission_tax()}</small
					>
				</div>
				<p class="m-0 text-[0.86rem] leading-snug text-zp-muted">
					{m.ab_commission_note_before()}<b class="font-semibold text-zp-ink"
						>{m.ab_commission_note_bold()}</b
					>{m.ab_commission_note_after()}
				</p>
			</div>
		</div>

		<!-- Form card -->
		<div
			class="zp-add-boat-card min-w-0 rounded-[20px] border border-white/50 bg-white/80 px-8 py-[30px] shadow-[0_50px_90px_-42px_rgba(20,17,48,0.55)] backdrop-blur-[18px] backdrop-saturate-[1.2] max-[820px]:px-6 max-[820px]:py-[26px] max-[680px]:px-4"
		>
			{#if !showOTPForm}
				<form
					class="flex flex-col gap-[15px] max-[820px]:gap-[13px]"
					novalidate
					onsubmit={handleSubmit}
				>
					<div>
						<h2 class="text-[1.4rem] leading-[1.12] text-zp-ink max-[720px]:text-[1.28rem]">
							{m.ab_form_h2()}
						</h2>
						<p class="mt-1.5 text-[0.92rem] text-zp-muted max-[720px]:mb-4">
							{m.ab_form_sub()}
						</p>
					</div>

					<div class="grid grid-cols-1 gap-[13px] min-[680px]:grid-cols-2">
						<fieldset class="m-0 flex min-w-0 flex-col gap-[7px] border-0 p-0">
							<legend class={labelClass}>
								{m.ab_label_boat_kind()}
							</legend>
							<div class="grid grid-cols-2 gap-[7px]">
								<label class="cursor-pointer">
									<input
										type="radio"
										name="boat-kind"
										class="peer sr-only"
										value={BoatsKindOptions.sailboat}
										bind:group={boat.kind}
										disabled={loading}
									/>
									<span
										class="{radioOptionClass} gap-2 px-2.5 py-[11px] text-[0.92rem] font-semibold"
									>
										<Sailboat
											class="size-[17px] {boat.kind === BoatsKindOptions.sailboat
												? 'text-zp-accent'
												: 'text-zp-muted'}"
											aria-hidden="true"
										/>
										{m.chunky_sharp_elk_fry()}
									</span>
								</label>
								<label class="cursor-pointer">
									<input
										type="radio"
										name="boat-kind"
										class="peer sr-only"
										value={BoatsKindOptions.motorboat}
										bind:group={boat.kind}
										disabled={loading}
									/>
									<span
										class="{radioOptionClass} gap-2 px-2.5 py-[11px] text-[0.92rem] font-semibold"
									>
										<Ship
											class="size-[17px] {boat.kind === BoatsKindOptions.motorboat
												? 'text-zp-accent'
												: 'text-zp-muted'}"
											aria-hidden="true"
										/>
										{m.dull_seemly_whale_love()}
									</span>
								</label>
							</div>
						</fieldset>

						<fieldset class="m-0 flex min-w-0 flex-col gap-[7px] border-0 p-0">
							<legend class={labelClass}>
								{m.ab_label_registry()}
							</legend>
							<div class="grid grid-cols-2 gap-[7px]">
								<label class="cursor-pointer">
									<input
										type="radio"
										name="boat-registry"
										class="peer sr-only"
										value={BoatsRegistryListOptions.seventh}
										bind:group={boat.registryList}
										disabled={loading}
									/>
									<span
										class="{radioOptionClass} gap-1 px-2.5 py-[11px] text-[0.92rem] font-semibold"
									>
										7ª <small class="font-medium text-zp-muted">{m.ab_registry_private()}</small>
									</span>
								</label>
								<label class="cursor-pointer">
									<input
										type="radio"
										name="boat-registry"
										class="peer sr-only"
										value={BoatsRegistryListOptions.sixth}
										bind:group={boat.registryList}
										disabled={loading}
									/>
									<span
										class="{radioOptionClass} gap-1 px-2.5 py-[11px] text-[0.92rem] font-semibold"
									>
										6ª <small class="font-medium text-zp-muted">{m.ab_registry_commercial()}</small>
									</span>
								</label>
							</div>
						</fieldset>
					</div>

					<div class="flex flex-col gap-[13px]">
						<div class="flex flex-col gap-[7px]">
							<label for="boat-name" class={labelClass}>
								{m.ab_label_boat_name()}
							</label>
							<input
								id="boat-name"
								type="text"
								bind:value={boat.name}
								disabled={loading}
								class={inputClasses('name')}
								aria-invalid={fieldErrors.name ? true : undefined}
								aria-describedby={fieldErrors.name ? 'boat-name-error' : undefined}
								oninput={() => clearFieldError('name')}
							/>
							{#if fieldErrors.name}
								<p id="boat-name-error" class="text-sm text-red-600">{fieldErrors.name}</p>
							{/if}
						</div>
						<div class="flex flex-col gap-[7px]">
							<label for="boat-port" class={labelClass}>
								{m.ab_label_port()}
							</label>
							<div class="relative">
								<svg
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="2"
									class="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-zp-muted"
									aria-hidden="true"
								>
									<path d="M21 10c0 6-9 12-9 12s-9-6-9-12a9 9 0 0118 0z" />
									<circle cx="12" cy="10" r="3" />
								</svg>
								<PortSelection
									id="boat-port"
									bind:bindableProp={boat.port}
									triggerClassName={portTriggerClass}
									placeholder={m.ab_port_select()}
									invalid={!!fieldErrors.port}
									invalidDescribedBy="boat-port-error"
								/>
							</div>
							{#if fieldErrors.port}
								<p id="boat-port-error" class="text-sm text-red-600">{fieldErrors.port}</p>
							{/if}
						</div>
					</div>

					{#if !isAuthenticated}
						<div class="flex flex-col gap-[7px]">
							<label for="bmail" class={labelClass}>
								{m.lofty_gross_larva_win()}
							</label>
							<input
								id="bmail"
								type="email"
								bind:value={emailAddress}
								disabled={loading}
								class={inputClasses('email')}
								aria-invalid={fieldErrors.email ? true : undefined}
								aria-describedby={fieldErrors.email ? 'bmail-error' : undefined}
								oninput={() => clearFieldError('email')}
							/>
							{#if fieldErrors.email}
								<p id="bmail-error" class="text-sm text-red-600">{fieldErrors.email}</p>
							{/if}
						</div>

						<div class="flex flex-col gap-2">
							<div class={checkboxRowClass}>
								<div class="flex h-[1.375em] shrink-0 items-center">
									<input
										type="checkbox"
										id="acceptTerms"
										bind:checked={acceptTerms}
										disabled={loading}
										aria-invalid={fieldErrors.acceptTerms ? true : undefined}
										aria-describedby={fieldErrors.acceptTerms ? 'accept-terms-error' : undefined}
										onchange={() => clearFieldError('acceptTerms')}
										class={checkboxClass}
									/>
								</div>
								<label for="acceptTerms" class={checkboxLabelClass}>
									{m.elegant_loud_iguana_slurp()}
									<a
										href={resolve(
											localizeHref('/terminos-y-condiciones') as '/terminos-y-condiciones'
										)}
										target="_blank"
										rel="noopener noreferrer"
										tabindex="-1"
										class="font-semibold text-zp-navy underline underline-offset-2"
									>
										{m.ab_terms_short()}
									</a>
									{m.east_loose_leopard_dream()}
									<a
										href={resolve(
											localizeHref('/politica-de-privacidad/') as '/politica-de-privacidad'
										)}
										target="_blank"
										rel="noopener noreferrer"
										tabindex="-1"
										class="font-semibold text-zp-navy underline underline-offset-2"
									>
										{m.each_basic_dachshund_value()}
									</a>.
								</label>
							</div>
							<div class={checkboxRowClass}>
								<div class="flex h-[1.375em] shrink-0 items-center">
									<input
										type="checkbox"
										id="acceptNewsletter"
										bind:checked={acceptNewsletter}
										disabled={loading}
										class={checkboxClass}
									/>
								</div>
								<label for="acceptNewsletter" class={checkboxLabelClass}>
									{m.mellow_pretty_rat_earn()}
								</label>
							</div>
							{#if fieldErrors.acceptTerms}
								<p id="accept-terms-error" class="text-sm text-red-600">
									{fieldErrors.acceptTerms}
								</p>
							{/if}
						</div>
					{/if}

					<VoucherInput context="adminFee" bind:applied={appliedVoucher} disabled={loading} />

					{#if formError}
						<div
							class="flex gap-3 rounded-[10px] border border-red-200 bg-red-50 px-3.5 py-3"
							role="alert"
						>
							<CircleAlert class="mt-0.5 size-4 shrink-0 text-red-600" />
							<div class="text-[0.88rem] text-red-900">
								<p class="font-semibold">{errorTitle}</p>
								<p class="mt-1">{errorMessage}</p>
							</div>
						</div>
					{/if}

					<button
						type="submit"
						disabled={loading}
						class="mt-0.5 inline-flex w-full items-center justify-center gap-2 rounded-[11px] border-none bg-zp-navy px-4 py-[15px] text-base font-semibold text-zp-paper transition-colors hover:bg-zp-ink disabled:opacity-70"
					>
						{#if loading}
							<ShipWheel class="size-4 animate-spin" />
							{m.ab_btn_sending()}
						{:else if isAuthenticated}
							{m.zesty_spry_gibbon_hint()}
							<ArrowIcon />
						{:else}
							{m.ab_btn_continue()}
							<ArrowIcon />
						{/if}
					</button>

					{#if !isAuthenticated}
						<p
							class="m-0 flex items-center justify-center gap-[7px] text-center text-[0.78rem] text-zp-muted"
						>
							<svg
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
								class="size-3.5 text-zp-green"
								aria-hidden="true"
							>
								<rect x="5" y="11" width="14" height="9" rx="2" />
								<path d="M8 11V7a4 4 0 018 0v4" />
							</svg>
							{m.ab_footnote()}
						</p>
					{/if}
				</form>
			{:else}
				<form class="flex flex-col gap-[15px]" novalidate onsubmit={verifyOTP}>
					<div class="text-center">
						<div
							class="mx-auto mb-5 grid size-[62px] place-items-center rounded-full bg-zp-green/14 text-zp-green"
						>
							<svg
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2.4"
								class="size-7"
								aria-hidden="true"
							>
								<path d="M20 6 9 17l-5-5" />
							</svg>
						</div>
						<h2 class="text-[1.45rem] leading-snug text-zp-ink">{m.ab_otp_h2()}</h2>
						<p class="mx-auto mt-3 max-w-[32ch] text-[0.98rem] leading-relaxed text-zp-muted">
							{m.ab_otp_p_before()}<span class="font-semibold text-zp-ink">{emailAddress}</span
							>{m.ab_otp_p_after()}
						</p>
					</div>

					<div class="flex flex-col gap-[7px]">
						<label for="otp-code" class={labelClass}>
							{m.ab_otp_label()}
						</label>
						<input
							id="otp-code"
							type="text"
							bind:value={otpCode}
							disabled={loading}
							maxlength={8}
							autocomplete="one-time-code"
							inputmode="numeric"
							class="{inputClasses('otpCode')} text-center font-mono text-lg tracking-[0.3em]"
							aria-invalid={fieldErrors.otpCode ? true : undefined}
							aria-describedby={fieldErrors.otpCode ? 'otp-code-error' : undefined}
							oninput={() => clearFieldError('otpCode')}
						/>
						{#if fieldErrors.otpCode}
							<p id="otp-code-error" class="text-sm text-red-600">{fieldErrors.otpCode}</p>
						{/if}
					</div>

					{#if formError}
						<div
							class="flex gap-3 rounded-[10px] border border-red-200 bg-red-50 px-3.5 py-3"
							role="alert"
						>
							<CircleAlert class="mt-0.5 size-4 shrink-0 text-red-600" />
							<div class="text-[0.88rem] text-red-900">
								<p class="font-semibold">{errorTitle}</p>
								<p class="mt-1">{errorMessage}</p>
							</div>
						</div>
					{/if}

					<div class="flex flex-col-reverse items-center justify-between gap-4 sm:flex-row">
						<button
							type="button"
							onclick={goBack}
							class="inline-flex items-center text-sm font-semibold text-zp-ink/80 hover:text-zp-ink"
						>
							<ArrowLeft class="mr-1 size-4" />
							{m.ab_back()}
						</button>
						<button
							type="submit"
							disabled={loading}
							class="inline-flex w-full items-center justify-center gap-2 rounded-[11px] bg-zp-navy px-6 py-[15px] text-base font-semibold text-zp-paper transition-colors hover:bg-zp-ink disabled:opacity-70 sm:w-auto"
						>
							{#if loading}
								<ShipWheel class="size-4 animate-spin" />
								{m.ab_otp_btn_verifying()}
							{:else}
								{m.ab_otp_btn_verify()}
								<ArrowIcon />
							{/if}
						</button>
					</div>
				</form>
			{/if}
		</div>
	</div>
</div>
